import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { 
  Search, Filter, RefreshCw, Eye, CheckCircle, XCircle, Clock, 
  Download, Mail, User, Phone, MapPin, GraduationCap, Calendar,
  IndianRupee, FileText
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const statusColors = {
  pending: 'bg-amber-100 text-amber-700',
  confirmed: 'bg-green-100 text-green-700',
  paid: 'bg-green-100 text-green-700',
  cancelled: 'bg-red-100 text-red-700',
  completed: 'bg-blue-100 text-blue-700',
  refunded: 'bg-slate-100 text-slate-700'
};

const programTypeLabels = {
  masterclass: 'Masterclass',
  examination: 'Examination',
  workshop: 'Workshop'
};

export default function ProgramRegistrationsTab({ token }) {
  const [registrations, setRegistrations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterType, setFilterType] = useState('all');
  const [filterStatus, setFilterStatus] = useState('all');
  const [selectedReg, setSelectedReg] = useState(null);
  const [showDetails, setShowDetails] = useState(false);

  const fetchRegistrations = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filterType !== 'all') params.append('program_type', filterType);
      if (filterStatus !== 'all') params.append('status', filterStatus);
      
      const res = await axios.get(`${API}/admin/program-registrations?${params}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setRegistrations(res.data.registrations || []);
    } catch (err) {
      toast.error('Failed to load registrations');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (token) fetchRegistrations();
  }, [token, filterType, filterStatus]);

  const updateStatus = async (id, newStatus) => {
    try {
      await axios.patch(`${API}/admin/program-registrations/${id}?status=${newStatus}`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(`Status updated to ${newStatus}`);
      fetchRegistrations();
      setShowDetails(false);
    } catch (err) {
      toast.error('Failed to update status');
    }
  };

  const filteredRegistrations = registrations.filter(reg => {
    if (!searchTerm) return true;
    const search = searchTerm.toLowerCase();
    return (
      reg.full_name?.toLowerCase().includes(search) ||
      reg.email?.toLowerCase().includes(search) ||
      reg.program_name?.toLowerCase().includes(search) ||
      reg.city?.toLowerCase().includes(search)
    );
  });

  const exportToCSV = () => {
    const headers = ['Name', 'Email', 'Phone', 'Program', 'Qualification', 'City', 'Status', 'Payment', 'Date'];
    const rows = filteredRegistrations.map(r => [
      r.full_name,
      r.email,
      r.phone,
      r.program_name,
      r.qualification,
      r.city,
      r.status,
      r.payment_status,
      new Date(r.created_at).toLocaleDateString()
    ]);
    
    const csv = [headers, ...rows].map(row => row.map(cell => `"${cell || ''}"`).join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `program_registrations_${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
  };

  return (
    <div data-testid="program-registrations-tab">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-6">
        <div>
          <h2 className="text-xl font-bold text-[#1B2A4A]">Program Registrations</h2>
          <p className="text-sm text-slate-500">Manage examination and program registrations</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={fetchRegistrations} className="rounded-sm">
            <RefreshCw className="h-4 w-4 mr-1" /> Refresh
          </Button>
          <Button variant="outline" size="sm" onClick={exportToCSV} className="rounded-sm">
            <Download className="h-4 w-4 mr-1" /> Export CSV
          </Button>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        <Card className="border-slate-200">
          <CardContent className="p-4">
            <p className="text-2xl font-bold text-[#1B2A4A]">{registrations.length}</p>
            <p className="text-xs text-slate-500">Total Registrations</p>
          </CardContent>
        </Card>
        <Card className="border-amber-200 bg-amber-50">
          <CardContent className="p-4">
            <p className="text-2xl font-bold text-amber-700">
              {registrations.filter(r => r.status === 'pending').length}
            </p>
            <p className="text-xs text-amber-600">Pending</p>
          </CardContent>
        </Card>
        <Card className="border-green-200 bg-green-50">
          <CardContent className="p-4">
            <p className="text-2xl font-bold text-green-700">
              {registrations.filter(r => r.payment_status === 'paid').length}
            </p>
            <p className="text-xs text-green-600">Paid</p>
          </CardContent>
        </Card>
        <Card className="border-blue-200 bg-blue-50">
          <CardContent className="p-4">
            <p className="text-2xl font-bold text-blue-700">
              ₹{registrations.filter(r => r.payment_status === 'paid').reduce((sum, r) => sum + (r.fee_amount || 0), 0).toLocaleString()}
            </p>
            <p className="text-xs text-blue-600">Total Revenue</p>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <div className="flex flex-col md:flex-row gap-3 mb-6">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
          <Input
            placeholder="Search by name, email, program, city..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10 rounded-sm"
          />
        </div>
        <Select value={filterType} onValueChange={setFilterType}>
          <SelectTrigger className="w-40 rounded-sm">
            <Filter className="h-4 w-4 mr-2" />
            <SelectValue placeholder="Type" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Types</SelectItem>
            <SelectItem value="masterclass">Masterclass</SelectItem>
            <SelectItem value="examination">Examination</SelectItem>
            <SelectItem value="workshop">Workshop</SelectItem>
          </SelectContent>
        </Select>
        <Select value={filterStatus} onValueChange={setFilterStatus}>
          <SelectTrigger className="w-40 rounded-sm">
            <SelectValue placeholder="Status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Status</SelectItem>
            <SelectItem value="pending">Pending</SelectItem>
            <SelectItem value="confirmed">Confirmed</SelectItem>
            <SelectItem value="cancelled">Cancelled</SelectItem>
            <SelectItem value="completed">Completed</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Registrations Table */}
      {loading ? (
        <div className="text-center py-12">
          <RefreshCw className="h-8 w-8 animate-spin mx-auto text-slate-400" />
          <p className="text-sm text-slate-500 mt-2">Loading registrations...</p>
        </div>
      ) : filteredRegistrations.length === 0 ? (
        <div className="text-center py-12 bg-slate-50 rounded-sm">
          <FileText className="h-12 w-12 text-slate-300 mx-auto mb-3" />
          <p className="text-slate-500">No registrations found</p>
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-100">
              <tr>
                <th className="px-4 py-3 text-left font-semibold text-slate-700">Registrant</th>
                <th className="px-4 py-3 text-left font-semibold text-slate-700">Program</th>
                <th className="px-4 py-3 text-left font-semibold text-slate-700">Qualification</th>
                <th className="px-4 py-3 text-left font-semibold text-slate-700">Location</th>
                <th className="px-4 py-3 text-center font-semibold text-slate-700">Fee</th>
                <th className="px-4 py-3 text-center font-semibold text-slate-700">Status</th>
                <th className="px-4 py-3 text-center font-semibold text-slate-700">Date</th>
                <th className="px-4 py-3 text-center font-semibold text-slate-700">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {filteredRegistrations.map((reg) => (
                <tr key={reg.id} className="hover:bg-slate-50">
                  <td className="px-4 py-3">
                    <p className="font-medium text-[#1B2A4A]">{reg.full_name}</p>
                    <p className="text-xs text-slate-500">{reg.email}</p>
                    <p className="text-xs text-slate-400">{reg.phone}</p>
                  </td>
                  <td className="px-4 py-3">
                    <p className="font-medium text-slate-700">{reg.program_name}</p>
                    <Badge variant="outline" className="text-xs mt-1">
                      {programTypeLabels[reg.program_type] || reg.program_type}
                    </Badge>
                  </td>
                  <td className="px-4 py-3">
                    <p className="text-slate-700">{reg.qualification}</p>
                    {reg.years_experience && (
                      <p className="text-xs text-slate-500">{reg.years_experience} years exp.</p>
                    )}
                  </td>
                  <td className="px-4 py-3">
                    <p className="text-slate-700">{reg.city}</p>
                    <p className="text-xs text-slate-500">{reg.country}</p>
                  </td>
                  <td className="px-4 py-3 text-center">
                    <p className="font-semibold text-[#1B2A4A]">₹{reg.fee_amount?.toLocaleString()}</p>
                    <Badge className={`text-xs mt-1 ${reg.payment_status === 'paid' ? 'bg-green-100 text-green-700' : 'bg-amber-100 text-amber-700'}`}>
                      {reg.payment_status}
                    </Badge>
                  </td>
                  <td className="px-4 py-3 text-center">
                    <Badge className={statusColors[reg.status] || 'bg-slate-100 text-slate-700'}>
                      {reg.status}
                    </Badge>
                  </td>
                  <td className="px-4 py-3 text-center text-slate-500 text-xs">
                    {new Date(reg.created_at).toLocaleDateString()}
                  </td>
                  <td className="px-4 py-3 text-center">
                    <Button 
                      size="sm" 
                      variant="outline" 
                      className="rounded-sm"
                      onClick={() => { setSelectedReg(reg); setShowDetails(true); }}
                    >
                      <Eye className="h-3 w-3 mr-1" /> View
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Details Dialog */}
      <Dialog open={showDetails} onOpenChange={setShowDetails}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Registration Details</DialogTitle>
          </DialogHeader>
          {selectedReg && (
            <div className="space-y-4">
              {/* Registrant Info */}
              <Card className="border-slate-200">
                <CardHeader className="pb-2">
                  <CardTitle className="text-base flex items-center gap-2">
                    <User className="h-4 w-4 text-[#C5A572]" /> Registrant Information
                  </CardTitle>
                </CardHeader>
                <CardContent className="grid md:grid-cols-2 gap-4 text-sm">
                  <div>
                    <p className="text-slate-500">Full Name</p>
                    <p className="font-medium">{selectedReg.full_name}</p>
                  </div>
                  <div>
                    <p className="text-slate-500">Email</p>
                    <p className="font-medium">{selectedReg.email}</p>
                  </div>
                  <div>
                    <p className="text-slate-500">Phone</p>
                    <p className="font-medium">{selectedReg.phone}</p>
                  </div>
                  <div>
                    <p className="text-slate-500">Qualification</p>
                    <p className="font-medium">{selectedReg.qualification}</p>
                  </div>
                  <div>
                    <p className="text-slate-500">Institution</p>
                    <p className="font-medium">{selectedReg.institution || '-'}</p>
                  </div>
                  <div>
                    <p className="text-slate-500">Experience</p>
                    <p className="font-medium">{selectedReg.years_experience ? `${selectedReg.years_experience} years` : '-'}</p>
                  </div>
                  <div>
                    <p className="text-slate-500">Location</p>
                    <p className="font-medium">{selectedReg.city}, {selectedReg.country}</p>
                  </div>
                  <div>
                    <p className="text-slate-500">Specialization</p>
                    <p className="font-medium">{selectedReg.specialization || '-'}</p>
                  </div>
                </CardContent>
              </Card>

              {/* Program & Payment Info */}
              <Card className="border-slate-200">
                <CardHeader className="pb-2">
                  <CardTitle className="text-base flex items-center gap-2">
                    <GraduationCap className="h-4 w-4 text-[#C5A572]" /> Program & Payment
                  </CardTitle>
                </CardHeader>
                <CardContent className="grid md:grid-cols-2 gap-4 text-sm">
                  <div>
                    <p className="text-slate-500">Program</p>
                    <p className="font-medium">{selectedReg.program_name}</p>
                  </div>
                  <div>
                    <p className="text-slate-500">Type</p>
                    <Badge variant="outline">{programTypeLabels[selectedReg.program_type]}</Badge>
                  </div>
                  <div>
                    <p className="text-slate-500">Fee Amount</p>
                    <p className="font-bold text-lg text-[#1B2A4A]">₹{selectedReg.fee_amount?.toLocaleString()}</p>
                  </div>
                  <div>
                    <p className="text-slate-500">Payment Status</p>
                    <Badge className={selectedReg.payment_status === 'paid' ? 'bg-green-100 text-green-700' : 'bg-amber-100 text-amber-700'}>
                      {selectedReg.payment_status}
                    </Badge>
                  </div>
                  {selectedReg.payment_id && (
                    <div className="md:col-span-2">
                      <p className="text-slate-500">Payment ID</p>
                      <p className="font-mono text-xs">{selectedReg.payment_id}</p>
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Message */}
              {selectedReg.message && (
                <Card className="border-slate-200">
                  <CardHeader className="pb-2">
                    <CardTitle className="text-base">Additional Message</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-sm text-slate-600">{selectedReg.message}</p>
                  </CardContent>
                </Card>
              )}

              {/* Actions */}
              <div className="flex gap-2 justify-end pt-2">
                {selectedReg.status === 'pending' && (
                  <>
                    <Button 
                      size="sm" 
                      className="bg-green-600 hover:bg-green-700 text-white rounded-sm"
                      onClick={() => updateStatus(selectedReg.id, 'confirmed')}
                    >
                      <CheckCircle className="h-4 w-4 mr-1" /> Confirm
                    </Button>
                    <Button 
                      size="sm" 
                      variant="outline"
                      className="text-red-600 border-red-300 hover:bg-red-50 rounded-sm"
                      onClick={() => updateStatus(selectedReg.id, 'cancelled')}
                    >
                      <XCircle className="h-4 w-4 mr-1" /> Cancel
                    </Button>
                  </>
                )}
                {selectedReg.status === 'confirmed' && (
                  <Button 
                    size="sm" 
                    className="bg-blue-600 hover:bg-blue-700 text-white rounded-sm"
                    onClick={() => updateStatus(selectedReg.id, 'completed')}
                  >
                    <CheckCircle className="h-4 w-4 mr-1" /> Mark Completed
                  </Button>
                )}
                <Button 
                  size="sm" 
                  variant="outline" 
                  className="rounded-sm"
                  onClick={() => window.open(`mailto:${selectedReg.email}?subject=IMCAC - ${selectedReg.program_name}`, '_blank')}
                >
                  <Mail className="h-4 w-4 mr-1" /> Email
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
